import { Icon } from '@iconify/react';
import { styled } from '@linaria/react';
import { Tooltip } from '@mantine/core';
import { useClipboard } from '@mantine/hooks';
import { memo, useMemo, type ReactNode } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';
import { useSlashCommands } from '../../../../api/agents-client/agent-client.hooks';
import { icons } from '../../../../assets/icons/icons';
import { Text } from '../../../../ui-library/typography/typography';
import { FloraFeedback } from '../flora-feedback/flora-feedback';
import { parseMentions } from '../flora-mention/flora-mention.helpers';
import { MentionTag } from '../flora-mention/mention-tag';
import {
  contentBlockToToolCall,
  findSubmittedAnswers,
  getActionablePrompts,
  getPlainTextContent,
  groupContentBlocksBySubagent,
  groupSegmentsBySubagent,
  isContentBlockArray,
  messageHasQuestionCard,
} from '../flora-message/flora-message.helpers';
import {
  AssistantBubble,
  AssistantRow,
  AvatarImg,
  AvatarWrap,
  UserBubble,
  UserRow,
} from '../flora-message/message-bubble.styles';
import { dedupeDocumentsByLatestRevision } from '../flora-document/flora-document.helpers';
import { FloraPNGImage } from '../flora-png-image';
import type {
  ContentBlock,
  Message,
  MessageSegment,
  SubagentInfo,
  ToolApproval,
  ToolApprovalDecision,
  ToolCall,
} from '../../flora.type';
import { MessageAttachmentChip } from './agent-attachment-chip';
import { AgentCollapsibleUserText } from './agent-collapsible-user-text';
import { AgentLinkPreview } from './agent-link-preview';
import { AgentCodeBlock } from './agent-code-block';
import { SlashCommandTag } from './slash-commands/slash-command-tag';
import { AgentDocumentCard } from './agent-document/agent-document-card';
import { AgentQuestionCard } from './agent-question-card';
import { AgentSubagentSection } from './agent-subagent-section';
import { AgentToolApproval } from './agent-tool-approval';
import { AgentToolCallGroup } from './agent-tool-call-group';

interface AgentMessageBubbleProps {
  question: Message;
  answer: Message | undefined;
  isStreaming: boolean;
  onPreviewDocument?: (documentId: string) => void;
  onFollowupClick?: (prompt: string) => void;
  onAnswerSubmit?: (jobId: string, answers: Record<string, string | string[]>) => void;
  onToolApproval?: (approvalDecision: ToolApprovalDecision, toolApproval: ToolApproval) => void;
  showFollowups?: boolean;
  chatHistoryId?: string;
  showFeedback?: boolean;
}

const REMARK_PLUGINS = [[remarkGfm, { singleTilde: false }], remarkBreaks] as never[];

const MARKDOWN_COMPONENTS: Components = {
  pre: AgentCodeBlock,
  a: ({ node, ...props }) => <AgentLinkPreview {...props} />,
};

/**
 * Renders a question/answer message pair in the Flora agent chat.
 *
 * Supports live streaming segments and persisted chat-history content blocks,
 * including tool calls, subagent sections, clarifying-question cards, tool
 * approvals, context-compaction banners, and interrupted-response dividers.
 *
 * @param {AgentMessageBubbleProps} props - The component props
 * @param {Message} props.question - The user's message
 * @param {Message | undefined} props.answer - Flora's response, if any
 * @param {boolean} props.isStreaming - Whether the answer is still streaming
 * @param {Function} [props.onPreviewDocument] - Called with a document id to open it in the preview rail
 * @param {Function} [props.onFollowupClick] - Called with a suggested follow-up prompt
 * @param {Function} [props.onAnswerSubmit] - Called with (jobId, answers) for clarifying questions
 * @param {Function} [props.onToolApproval] - Called with (approvalDecision, toolApproval) for HITL approvals
 * @param {boolean} [props.showFollowups] - Whether to render suggested follow-up chips
 * @param {string} [props.chatHistoryId] - Current chat id, passed to the feedback control as `chat_history_id`
 * @param {boolean} [props.showFeedback] - Whether to render the thumbs up/down feedback control (latest live, completed turn only)
 * @returns {JSX.Element} The rendered message bubble pair
 *
 * @example
 * <AgentMessageBubble question={question} answer={answer} isStreaming={false} />
 */
function AgentMessageBubbleComponent({
  question,
  answer,
  isStreaming,
  onPreviewDocument,
  onFollowupClick,
  onAnswerSubmit,
  onToolApproval,
  showFollowups,
  chatHistoryId,
  showFeedback,
}: AgentMessageBubbleProps) {
  const clipboard = useClipboard();
  const { commands } = useSlashCommands();
  const commandNames = useMemo(() => new Set(commands.map((command) => command.name.toLowerCase())), [commands]);
  // Flora appends a fresh Document to the turn on every edit/re-insert, so a doc
  // iterated on N times yields N+1 cards for the same file. Collapse to one card
  // per document (latest revision) — see dedupeDocumentsByLatestRevision.
  const dedupedDocuments = useMemo(() => dedupeDocumentsByLatestRevision(answer?.documents ?? []), [answer?.documents]);

  const hasQuestionCard = useMemo(() => messageHasQuestionCard(answer), [answer]);

  const renderMarkdown = (text: string, key: string) => (
    <ReactMarkdown key={key} remarkPlugins={REMARK_PLUGINS} components={MARKDOWN_COMPONENTS}>
      {text}
    </ReactMarkdown>
  );

  const renderInterrupted = (key: string) => (
    <InterruptedDivider key={key}>
      <Text size="tiny" color="inherit">
        Interrupted by user
      </Text>
    </InterruptedDivider>
  );

  const renderError = (key: string, message: string) => (
    <ErrorBubble key={key}>
      <Icon icon="material-symbols:error-outline" width={16} height={16} />
      <Text size="small" color="inherit">
        {message}
      </Text>
    </ErrorBubble>
  );

  // Render a subagent's inner items, coalescing contiguous tool calls into the
  // same grouped tool-call block used at the top level (the `task`/clarifying
  // wrappers are skipped). `keyPrefix` and `isLive` are threaded from the caller.
  const renderSubagentSegments = (segments: MessageSegment[], keyPrefix: string, isLive: boolean): ReactNode[] => {
    const children: ReactNode[] = [];
    let toolBuffer: ToolCall[] = [];
    let toolBufferStart = -1;
    const flushTools = () => {
      if (toolBuffer.length > 0) {
        children.push(
          <AgentToolCallGroup
            key={`${keyPrefix}-tools-${toolBufferStart}-${toolBuffer[0].id}`}
            toolCalls={toolBuffer}
            isLive={isLive}
          />,
        );
        toolBuffer = [];
        toolBufferStart = -1;
      }
    };
    segments.forEach((seg, si) => {
      if (seg.type === 'tool_call') {
        if (seg.toolCall.tool_name === 'task' || seg.toolCall.tool_name === 'ask_user_questions') return;
        if (toolBuffer.length === 0) toolBufferStart = si;
        toolBuffer.push(seg.toolCall);
        return;
      }
      flushTools();
      if (seg.type === 'text') children.push(renderMarkdown(seg.content, `${keyPrefix}-text-${si}`));
      else if (seg.type === 'interrupted') children.push(renderInterrupted(`${keyPrefix}-interrupted-${si}`));
    });
    flushTools();
    return children;
  };

  const renderSubagentBlocks = (blocks: ContentBlock[], keyPrefix: string): ReactNode[] => {
    const children: ReactNode[] = [];
    let toolBuffer: ToolCall[] = [];
    let toolBufferStart = -1;
    const flushTools = () => {
      if (toolBuffer.length > 0) {
        children.push(
          <AgentToolCallGroup
            key={`${keyPrefix}-tools-${toolBufferStart}-${toolBuffer[0].id}`}
            toolCalls={toolBuffer}
            isLive={false}
          />,
        );
        toolBuffer = [];
        toolBufferStart = -1;
      }
    };
    blocks.forEach((block, bi) => {
      if (block.type === 'tool_call') {
        if (block.name === 'task' || block.name === 'ask_user_questions') return;
        if (toolBuffer.length === 0) toolBufferStart = bi;
        toolBuffer.push(contentBlockToToolCall(block, `${keyPrefix}-tool-${bi}`));
        return;
      }
      flushTools();
      if (block.type === 'text') children.push(renderMarkdown(block.text, `${keyPrefix}-text-${bi}`));
      else if (block.type === 'interrupted') children.push(renderInterrupted(`${keyPrefix}-interrupted-${bi}`));
    });
    flushTools();
    return children;
  };

  // The subagent's final report (or error). The subagent's own model tokens are
  // suppressed upstream, so this `output` from the task result is the only place
  // its conclusion surfaces — shown at the bottom of the (collapsed) section.
  const renderSubagentOutcome = (subagent: SubagentInfo, keyPrefix: string): ReactNode | null => {
    if (subagent.status === 'error' && subagent.error) {
      return renderError(`${keyPrefix}-error`, subagent.error);
    }
    if (subagent.output) {
      return (
        <SubagentResult key={`${keyPrefix}-result`}>
          <SubagentResultLabel>Result</SubagentResultLabel>
          {renderMarkdown(subagent.output, `${keyPrefix}-result-md`)}
        </SubagentResult>
      );
    }
    return null;
  };

  const renderAnswer = () => {
    if (!answer) return null;

    // Streaming segments (live messages)
    if (answer.segments && answer.segments.length > 0) {
      const groups = groupSegmentsBySubagent(answer.segments);
      const segments = answer.segments;
      // Only the trailing unresolved prompt takes input; earlier cards on this
      // message were already answered or decided (a later question or a
      // reconnect replay must not re-arm them).
      const actionable = getActionablePrompts(answer);
      const elements: ReactNode[] = [];
      // Coalesce consecutive top-level tool calls into a single grouped block;
      // flush the buffer whenever a non-tool-call item breaks the run. The key
      // embeds the run's start index so two runs can never collide on key even
      // if the backend reuses a tool id.
      let toolBuffer: ToolCall[] = [];
      let toolBufferStart = -1;
      const flushTools = () => {
        if (toolBuffer.length > 0) {
          elements.push(
            <AgentToolCallGroup
              key={`tools-${toolBufferStart}-${toolBuffer[0].id}`}
              toolCalls={toolBuffer}
              isLive={isStreaming}
            />,
          );
          toolBuffer = [];
          toolBufferStart = -1;
        }
      };

      groups.forEach((group, gi) => {
        if (group.type === 'standalone') {
          const seg = group.segment;
          if (seg.type === 'tool_call') {
            // Skip only the invisible clarifying-question tool. A `task` call is
            // normally dropped by grouping (its subagent section represents it);
            // one that reaches here has no section (e.g. a reconnect's flat
            // replay), so render it as a normal tool call to keep its output.
            if (seg.toolCall.tool_name === 'ask_user_questions') return;
            if (toolBuffer.length === 0) toolBufferStart = gi;
            toolBuffer.push(seg.toolCall);
            return;
          }
          flushTools();
          switch (seg.type) {
            case 'text':
              elements.push(renderMarkdown(seg.content, `seg-text-${gi}`));
              break;
            case 'context_compacted':
              elements.push(
                seg.finished ? (
                  <CompactedDivider key={`compacted-${gi}`}>
                    <Icon icon="material-symbols:compress" width={14} height={14} />
                    <Text size="tiny" color="inherit">
                      Context compacted
                    </Text>
                  </CompactedDivider>
                ) : (
                  <CompactingBanner key={`compacting-${gi}`}>
                    <CompactingSpinner />
                    <Text size="tiny" color="inherit">
                      Summarizing...
                    </Text>
                  </CompactingBanner>
                ),
              );
              break;
            case 'interrupted':
              elements.push(renderInterrupted(`interrupted-${gi}`));
              break;
            case 'error':
              elements.push(renderError(`error-${gi}`, seg.message));
              break;
            case 'ask_user_questions': {
              const canSubmit = seg === actionable.question && Boolean(onAnswerSubmit);
              const pendingJobId = answer.pendingJobId;
              elements.push(
                <AgentQuestionCard
                  key={`question-card-${gi}`}
                  questions={seg.questions}
                  onSubmit={(answers) => {
                    if (canSubmit && pendingJobId) {
                      onAnswerSubmit?.(pendingJobId, answers);
                    }
                  }}
                  disabled={!canSubmit}
                  submittedAnswers={findSubmittedAnswers(segments, seg)}
                  rejection={canSubmit ? answer.promptRejection : undefined}
                />,
              );
              break;
            }
            case 'tool_approval': {
              elements.push(
                <AgentToolApproval
                  key={`tool-approval-${gi}`}
                  toolApproval={seg.toolApproval}
                  decision={seg.decision}
                  onApprove={seg === actionable.approval ? onToolApproval : undefined}
                  rejection={seg === actionable.approval ? answer.promptRejection : undefined}
                />,
              );
              break;
            }
            default:
              break;
          }
        } else {
          flushTools();
          const children = renderSubagentSegments(group.segments, `sa-${gi}`, isStreaming);
          const outcome = renderSubagentOutcome(group.subagent, `sa-${gi}`);
          if (outcome) children.push(outcome);
          elements.push(
            <AgentSubagentSection key={`sa-${gi}`} subagent={group.subagent}>
              {children}
            </AgentSubagentSection>,
          );
        }
      });
      flushTools();
      return elements;
    }

    // ContentBlock array (chat history)
    if (isContentBlockArray(answer.content)) {
      const blkGroups = groupContentBlocksBySubagent(answer.content);
      const elements: ReactNode[] = [];
      let toolBuffer: ToolCall[] = [];
      let toolBufferStart = -1;
      const flushTools = () => {
        if (toolBuffer.length > 0) {
          elements.push(
            <AgentToolCallGroup
              key={`blk-tools-${toolBufferStart}-${toolBuffer[0].id}`}
              toolCalls={toolBuffer}
              isLive={false}
            />,
          );
          toolBuffer = [];
          toolBufferStart = -1;
        }
      };

      blkGroups.forEach((group, gi) => {
        if (group.type === 'standalone') {
          const block = group.block;
          if (block.type === 'tool_call') {
            // Skip only the invisible clarifying-question tool. An `is_subagent`
            // task block is already consumed as a group start by the grouping
            // helper; a task block that reaches here has no section, so render it
            // as a normal tool call rather than dropping its output.
            if (block.name === 'ask_user_questions') return;
            if (toolBuffer.length === 0) toolBufferStart = gi;
            toolBuffer.push(contentBlockToToolCall(block, `blk-tool-${gi}`));
            return;
          }
          flushTools();
          switch (block.type) {
            case 'text':
              elements.push(renderMarkdown(block.text, `blk-text-${gi}`));
              break;
            case 'context_compacted':
              elements.push(
                <CompactedDivider key={`compacted-blk-${gi}`}>
                  <Icon icon="material-symbols:compress" width={14} height={14} />
                  <Text size="tiny" color="inherit">
                    Context compacted
                  </Text>
                </CompactedDivider>,
              );
              break;
            case 'interrupted':
              elements.push(renderInterrupted(`blk-interrupted-${gi}`));
              break;
            case 'ask_user_questions': {
              const nextAnswerBlk = blkGroups
                .slice(gi + 1)
                .find((g) => g.type === 'standalone' && g.block.type === 'user_answers');
              const submittedAnswers =
                nextAnswerBlk?.type === 'standalone' && nextAnswerBlk.block.type === 'user_answers'
                  ? nextAnswerBlk.block.answers
                  : undefined;
              elements.push(
                <AgentQuestionCard
                  key={`blk-question-${gi}`}
                  questions={block.data.questions}
                  onSubmit={() => {}}
                  disabled
                  submittedAnswers={submittedAnswers}
                />,
              );
              break;
            }
            default:
              break;
          }
        } else {
          flushTools();
          const blkChildren = renderSubagentBlocks(group.blocks, `sa-blk-${gi}`);
          const blkOutcome = renderSubagentOutcome(group.subagent, `sa-blk-${gi}`);
          if (blkOutcome) blkChildren.push(blkOutcome);
          elements.push(
            <AgentSubagentSection key={`sa-blk-${gi}`} subagent={group.subagent}>
              {blkChildren}
            </AgentSubagentSection>,
          );
        }
      });
      flushTools();
      return elements;
    }

    // Plain string (legacy)
    const legacyContent = typeof answer.content === 'string' ? answer.content : JSON.stringify(answer.content, null, 2);
    return renderMarkdown(legacyContent, 'legacy');
  };

  return (
    <BubbleGroup>
      <UserRow>
        <UserBubble>
          {question.attachments && question.attachments.length > 0 && (
            <UserAttachments>
              {question.attachments.map((attachment) => (
                <MessageAttachmentChip
                  key={attachment.document_id}
                  filename={attachment.filename}
                  docType={attachment.doc_type}
                />
              ))}
            </UserAttachments>
          )}
          {typeof question.content === 'string' ? (
            <AgentCollapsibleUserText>
              {(() => {
                const parsedSegments = parseMentions(question.content);
                const firstTextSegmentIndex = parsedSegments.findIndex((segment) => segment.type === 'text');

                return parsedSegments.map((segment, i) => {
                  if (segment.type === 'mention') {
                    return <MentionTag key={i} mentionType={segment.mentionType} name={segment.name} />;
                  }
                  // Check for /commandname at the start of a text segment
                  const slashMatch = segment.value.match(/^(\s*)\/([\w-]+)([\s\S]*)$/);
                  if (slashMatch && i === firstTextSegmentIndex && commandNames.has(slashMatch[2].toLowerCase())) {
                    const [, leading, cmdName, rest] = slashMatch;
                    return (
                      <span key={i}>
                        {leading}
                        <SlashCommandTag name={cmdName} />
                        {rest}
                      </span>
                    );
                  }
                  return <span key={i}>{segment.value}</span>;
                });
              })()}
            </AgentCollapsibleUserText>
          ) : (
            ''
          )}
        </UserBubble>
      </UserRow>

      {answer && (
        <AssistantRow>
          <AvatarWrap>
            <AvatarImg src={icons.iconFlora} alt="Flora" />
          </AvatarWrap>
          <AssistantBubble data-has-questions={hasQuestionCard ? 'true' : undefined}>
            {renderAnswer()}

            {answer.images && answer.images.length > 0 && (
              <ImagesWrap>
                {answer.images.map((img, i) => (
                  <FloraPNGImage key={i} imageData={img} index={i} />
                ))}
              </ImagesWrap>
            )}

            {dedupedDocuments.length > 0 && (
              <DocsWrap>
                {dedupedDocuments.map((doc, i) => (
                  <AgentDocumentCard key={doc.document_id || i} document={doc} onPreviewDocument={onPreviewDocument} />
                ))}
              </DocsWrap>
            )}

            {isStreaming && (
              <StreamingDots>
                <Dot style={{ animationDelay: '0ms' }} />
                <Dot style={{ animationDelay: '150ms' }} />
                <Dot style={{ animationDelay: '300ms' }} />
              </StreamingDots>
            )}

            {!isStreaming && (
              <AnswerActions>
                <Tooltip label={clipboard.copied ? 'Copied!' : 'Copy response'} position="top" withinPortal={false}>
                  <CopyBtn
                    onClick={() => clipboard.copy(getPlainTextContent(answer))}
                    type="button"
                    aria-label="Copy response"
                  >
                    <Icon
                      icon={clipboard.copied ? 'material-symbols:check' : 'material-symbols:content-copy-outline'}
                      width={16}
                      height={16}
                    />
                  </CopyBtn>
                </Tooltip>
                {showFeedback && chatHistoryId && <FloraFeedback key={chatHistoryId} chatHistoryId={chatHistoryId} />}
              </AnswerActions>
            )}

            {showFollowups && !isStreaming && answer.suggestedFollowups && answer.suggestedFollowups.length > 0 && (
              <FollowupsWrap>
                {answer.suggestedFollowups.map((followup, i) => (
                  <FollowupChip key={i} onClick={() => onFollowupClick?.(followup)} type="button">
                    {followup}
                  </FollowupChip>
                ))}
              </FollowupsWrap>
            )}
          </AssistantBubble>
        </AssistantRow>
      )}
    </BubbleGroup>
  );
}

const BubbleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 8px;
`;

const AnswerActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 10px;
`;

const CopyBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: var(--ink-lighter);
  border-radius: 4px;
  transition: color 0.15s, background 0.15s;

  &:hover {
    color: var(--ink-base);
    background: var(--flora-hover-subtle);
  }
`;

const ImagesWrap = styled.div`
  margin-top: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const DocsWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
`;

const UserAttachments = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
`;

const StreamingDots = styled.div`
  display: flex;
  gap: 4px;
  margin-top: 8px;
`;

const Dot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--secondary-base);
  opacity: 0.4;
  animation: dotPulse 1s ease-in-out infinite;

  @keyframes dotPulse {
    0%, 80%, 100% { opacity: 0.25; transform: scale(0.8); }
    40% { opacity: 1; transform: scale(1); }
  }
`;

const CompactingBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  margin: 12px 0;
  border-radius: 6px;
  background: var(--sky-lighter);
  color: var(--ink-lighter);
  font-size: 12px;

  [data-theme='dark'] & {
    background: rgba(255, 255, 255, 0.06);
    color: #aaa;
  }
`;

const CompactingSpinner = styled.span`
  width: 14px;
  height: 14px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: compacting-spin 0.8s linear infinite;
  flex-shrink: 0;

  @keyframes compacting-spin {
    to { transform: rotate(360deg); }
  }
`;

const CompactedDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  margin: 12px 0;
  border-radius: 6px;
  background: var(--sky-lighter);
  color: var(--ink-lighter);
  font-size: 12px;

  [data-theme='dark'] & {
    background: rgba(255, 255, 255, 0.06);
    color: #aaa;
  }
`;

const ErrorBubble = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 12px 0;
  padding: 10px 12px;
  border: 1px solid var(--red-lighter);
  border-radius: 8px;
  background: var(--red-lightest);
  color: var(--red-darkest);

  [data-theme='dark'] & {
    border-color: rgba(255, 255, 255, 0.12);
    background: rgba(255, 82, 71, 0.12);
    color: var(--red-lighter);
  }
`;

const SubagentResult = styled.div`
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid var(--flora-subagent-border-accent);
`;

const SubagentResultLabel = styled.div`
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--ink-lighter);
  margin-bottom: 6px;
`;

const InterruptedDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 12px 0;
  color: var(--ink-lighter);
  font-size: 12px;

  &::before,
  &::after {
    content: '';
    flex: 1;
    border-top: 1px solid var(--sky-light);
  }

  [data-theme='dark'] & {
    color: #aaa;

    &::before,
    &::after {
      border-color: rgba(255, 255, 255, 0.12);
    }
  }
`;

const FollowupsWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid var(--sky-light);
`;

export const FollowupChip = styled.button`
  padding: 8px 16px;
  border: 1px solid var(--secondary-light);
  border-radius: 18px;
  background: var(--base-white);
  color: var(--secondary-dark);
  font-size: 13px;
  font-weight: 500;
  line-height: 1.4;
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: left;

  &:hover {
    border-color: var(--secondary-base);
    background: var(--secondary-lightest);
    color: var(--secondary-darkest);
  }
`;

/**
 * Memoized {@link AgentMessageBubbleComponent}. Lets React skip re-rendering
 * completed (referentially stable) message bubbles when a new streaming chunk
 * only updates the last message.
 */
export const AgentMessageBubble = memo(AgentMessageBubbleComponent);
